import type { RngState, Seat } from "@bing/engine-core";

/* ------------------------------------------------------------------ *
 * منچ — the Persian form of the German "Mensch ärgere Dich nicht".
 *
 * NOT international Ludo: the board is the Mensch board, a 40-square
 * main track with a FOUR-square home row per player (Ludo uses 52 + 6).
 * Getting that wrong would produce a different game, so the geometry
 * lives here as named constants rather than scattered magic numbers.
 * ------------------------------------------------------------------ */

/** Squares on the shared main track, travelled clockwise. */
export const TRACK_LEN = 40;
/** Each player's private run-in (steps 40..43 in the position model). */
export const HOME_LEN = 4;
/** Tokens (مهره) per player. */
export const TOKENS_PER_PLAYER = 4;
/**
 * Board ARMS — the most seats a منچ board can carry, and therefore the number of
 * start squares, home runs and yards painted on it. This is a property of the
 * BOARD, not of the table: a 2- or 3-player game uses the same board and simply
 * leaves the unused arms empty. For "how many are playing", see
 * {@link MenschConfig.playerCount}.
 */
export const MENSCH_SEATS = 4;
/** Player counts a منچ table supports. */
export const MENSCH_PLAYER_COUNTS = [2, 3, 4] as const;
export type MenschPlayerCount = (typeof MENSCH_PLAYER_COUNTS)[number];
/** Distance between adjacent players' start squares (40 ÷ 4). */
export const START_SPACING = TRACK_LEN / MENSCH_SEATS;
/** A token that has not entered play yet. */
export const YARD = -1;
/** The last home square; a token here is finished. Journey is 0..43. */
export const FINAL_STEP = TRACK_LEN + HOME_LEN - 1;
/** Only this roll releases a token from the yard. */
export const RELEASE_ROLL = 6;

/**
 * A token's position, measured as STEPS TRAVELLED FROM ITS OWN START SQUARE:
 *
 *   -1        in the yard (خانه) — not yet entered
 *   0..39     on the main track, at absolute square (seat * 10 + step) mod 40
 *   40..43    in this seat's private home row
 *   43        finished
 *
 * Everything awkward falls out of this as arithmetic: the exact-roll finish is
 * `step + die <= 43`, and home rows can never collide because each seat owns
 * its own 40..43 range.
 */
export type MenschStep = number;

/**
 * A player intent. A turn is: `roll` → `move` (or `pass` when nothing is legal).
 * The server rolls from `rngState`, so a client can never choose its own number.
 */
export type MenschMove =
  | { readonly kind: "roll" }
  | { readonly kind: "move"; readonly token: number }
  | { readonly kind: "pass" };

/** Rule flags. Defaults are the sourced منچ/Mensch canon — see the design spec §2. */
export interface MenschConfig {
  /**
   * How many seats are actually playing (2, 3 or 4). Seats are always `0..playerCount-1`
   * so they line up with the room's own seat indices; which BOARD ARM each one occupies
   * is a separate question — see `armOf` in ./rules.
   */
  readonly playerCount: number;
  /**
   * R9 — a roll that lands exactly on an opponent MAY be played as that capture
   * («Schmeißen ist Pflicht» makes it compulsory).
   *
   * DEFAULT OFF, deliberately. The strict rule removes the player's decision exactly
   * when the position is most interesting: with a capture available it is the ONLY
   * legal move, so a player holding two live tokens cannot choose to advance the other
   * one. Choosing your token is the game's main tactical lever, so we keep it. Set this
   * true for the strict continental rule.
   */
  readonly mandatoryCapture: boolean;
  /**
   * V1 — how many times a player may roll while EVERY one of their tokens is still
   * in the yard, looking for a 6.
   *
   * DEFAULT 1 — the STRICT core rule (operator decision, 2026-07-22). We previously
   * shipped the widely-played 3-attempt variation because one attempt leaves a player
   * sitting out several turns early on; the operator asked for the strict rule, so the
   * cost is accepted. Raise it per-table to restore the variation.
   */
  readonly yardRollAttempts: number;
  /**
   * R13 — a token standing on its OWN start square is SAFE: no opponent may capture it there,
   * and therefore no opponent may LAND there at all.
   *
   * DEFAULT OFF (operator decision, 2026-07-22). It IS a sourced rule — Persian منچ sources state
   * it plainly («خانه شروع هر بازیکن، خانه امن در نظر گرفته می‌شود…») and general Ludo rules agree
   * the four coloured start squares are safe; strict German «Mensch ärgere Dich nicht» does not.
   * We shipped it on, then turned it off, because ON it leaves a HALF-STATE: protection only
   * works if the start square is ALSO excluded from step-counting, and it cannot be (excluding it
   * would give players different track lengths and break the position model). With protection but
   * no exclusion, a square can sit permanently un-landable and quietly remove legal moves.
   * So: the coloured start squares are ordinary squares. Capture is universal on the main track,
   * including a token sitting on its own start square. Set this true for the safe-square variant.
   *
   * WHY, WHEN ON, LANDING IS REFUSED RATHER THAN SHARED: if the capture were merely suppressed the
   * two tokens would have to coexist on one square — and no two tokens ever share a square in this
   * engine (R8 forbids your own, R7 removes an opponent's).
   */
  readonly safeStartSquare: boolean;
}

export const DEFAULT_MENSCH_CONFIG: MenschConfig = {
  playerCount: MENSCH_SEATS,
  mandatoryCapture: false,
  yardRollAttempts: 1,
  safeStartSquare: false,
};

/** What just happened, for the client's motion layer (§5 of the design spec). */
export interface MenschEvent {
  /** The seat that acted. */
  readonly seat: Seat;
  /** Which of that seat's tokens moved. */
  readonly token: number;
  /** Where it came from / went to, in steps. */
  readonly from: MenschStep;
  readonly to: MenschStep;
  /** Set when this move sent an opponent home — drives the capture motion (M5). */
  readonly captured?: { readonly seat: Seat; readonly token: number };
  /** True when the token left the yard on a 6 (M2 release, not an ordinary hop). */
  readonly released: boolean;
  /** True when the token landed on its FINAL home square (step 43) — the end of its journey. */
  readonly finished: boolean;
  /**
   * True when the token just ENTERED the home row (crossed from the track into 40..43).
   *
   * This — not `finished` — is "a token got home". `finished` is only step 43, the innermost
   * square, so a token settling on 40, 41 or 42 produced no event at all and the success cue
   * fired for exactly one token per game. Winning needs all four home squares filled, so all
   * four arrivals deserve the cue.
   */
  readonly enteredHome: boolean;
}

/** The authoritative state. Pure data — no clocks, no I/O. */
export interface MenschState {
  /** `tokens[seat][i]` = that token's step. Always `MENSCH_SEATS × TOKENS_PER_PLAYER`. */
  readonly tokens: readonly (readonly MenschStep[])[];
  readonly turn: Seat;
  /** The rolled value awaiting play; `null` before rolling. */
  readonly die: number | null;
  /** Roll attempts spent this turn (only ever >1 while yard-locked — see V1). */
  readonly rollsUsed: number;
  /**
   * MONOTONIC count of every die roll in the game. The client keys its throw animation on
   * this, NOT on the die's value: rolling a 3 twice in a row is two throws and must animate
   * twice, but the value never changes. Never resets.
   */
  readonly rollCount: number;
  /**
   * MONOTONIC count of every MOVE applied. The client keys its motion on this, NOT on
   * `lastEvent`'s object identity: every frame arrives as fresh JSON over the socket, so the
   * event is a NEW object with identical contents on every single state update — comparing
   * identity re-fires the walk, the ripple, the capture notice and the sound on the next
   * unrelated frame. Never resets.
   */
  readonly moveCount: number;
  readonly rngState: RngState;
  readonly winner: Seat | null;
  readonly config: MenschConfig;
  /** The last applied move, for motion. */
  readonly lastEvent: MenschEvent | null;
  /**
   * WHICH kind of action was applied last. `lastEvent` only describes a MOVE, so after a
   * roll it is stale — which meant the pacing delay after a throw was whatever the previous
   * move happened to ask for (or the room's default). A bot therefore played its token
   * before its own die had finished landing. This makes "a roll just happened" explicit.
   */
  readonly lastAction: "roll" | "move" | "pass" | null;
}

/** The per-seat projection. منچ is perfect information — nothing is redacted. */
export interface MenschView {
  readonly you: Seat;
  readonly turn: Seat;
  readonly tokens: readonly (readonly MenschStep[])[];
  readonly die: number | null;
  /** Indices of YOUR tokens that may legally move with the current die. */
  readonly legalTokens: readonly number[];
  /** True when it is your turn and you still have to roll — at the start of the turn OR after an
   *  unplayable 6 (R6: a 6 always earns another roll, so an unplayable 6 does NOT pass). */
  readonly canRoll: boolean;
  /** True when the die is rolled, nothing can be played, and the turn will PASS (a non-6 dud).
   *  FALSE for an unplayable 6 — that rolls again (see {@link mustReroll}). */
  readonly noLegalMove: boolean;
  /** R6 — you rolled a 6 with no legal move: the turn STAYS with you and you must roll again (a 6
   *  ALWAYS earns another roll, with no cap). The client shows "you rolled a 6 — roll again". */
  readonly mustReroll: boolean;
  /** {@link MenschState.rollCount} — the client animates a throw whenever this changes. */
  readonly rollCount: number;
  /** {@link MenschState.moveCount} — the client animates a MOVE whenever this changes. */
  readonly moveCount: number;
  /** Which seat threw the die now showing, so the client can say WHOSE roll it is. */
  readonly roller: Seat | null;
  /** Tokens each seat has finished (all four = a win). */
  readonly finished: readonly number[];
  readonly status: "playing" | "over";
  readonly winner?: Seat;
  readonly lastEvent: MenschEvent | null;
}
